import React from 'react';
import { fmtTime } from '../state/format';
import { isTimingSet } from '../state/derive';
import { PickSelect, SearchSelect } from './controls';
import {
  EVENT_SIDES, KIND_CHIP, KIND_CHIP_SHORT, ORDER_SIDE_CHIP, PACKAGE, RITUAL, SIDE,
} from '../../constants';

const e = React.createElement;

// Said on hover, so a card that refuses to be dragged says why rather than
// reading as a card the drag is broken on.
const PINNED_WHY = 'Getting ready is shot right before the wedding, '
  + 'so this one cannot be moved';

/**
 * One event in the order phase. Dragging is how the order changes, from
 * anywhere on the card, with a mouse or a finger. Alt with the arrow keys moves a focused card,
 * because a drag alone is not reachable from a keyboard.
 */
const OrderCard = ({
  entry, position, locked, pinned, isPhone, editing, dragId, overId, error, shot,
  onEdit, onRename, onPointerDown, onRemove, onNudge, onSide, ritualChoices,
  spareRituals,
}) => {
  const pkg = entry.kind !== RITUAL;
  const timing = isTimingSet(entry)
    ? `${fmtTime(entry.start)} to ${fmtTime(entry.end)}`
    : 'Timings not set yet';

  const side = ORDER_SIDE_CHIP[entry.side] ? entry.side : SIDE.BOTH;

  return e(
    'div',
    {
      className: `wd-ocard${locked || pinned ? '' : ' is-movable'}${pinned ? ' is-pinned' : ''}${dragId === String(entry.id) ? ' is-dragging' : ''}${
        overId === String(entry.id) && dragId && dragId !== String(entry.id) ? ' is-over' : ''
      }`,
      'data-wd-entry': String(entry.id),
      // On the card rather than on the flag alone, so hovering anywhere on
      // a held card says why it will not move.
      title: pinned ? PINNED_WHY : undefined,
      tabIndex: 0,
      'aria-label': pinned
        ? `${position + 1}. ${entry.name || 'New ritual'}. Always just before the wedding.`
        : `${position + 1}. ${entry.name || 'New ritual'}. Alt plus arrow keys to move.`,
      onPointerDown: pinned ? undefined : (ev) => onPointerDown(ev, entry.id),
      onKeyDown: (ev) => {
        if (pinned) return;
        if (!ev.altKey || (ev.key !== 'ArrowUp' && ev.key !== 'ArrowDown')) return;
        if (ev.target !== ev.currentTarget) return;
        ev.preventDefault();
        onNudge(entry.id, ev.key === 'ArrowUp' ? -1 : 1);
      },
    },
    [
      // The grip says the card moves. A drag starts anywhere on the card.
      // A pinned one has nowhere to go, so it says that instead.
      pinned
        ? e('span', { className: 'wd-pin', key: 'handle' }, '\u2691')
        : e('span', { className: 'wd-handle', key: 'handle', 'data-wd-grip': 'true', 'aria-hidden': 'true' }, '⠿'),
      e('span', { className: 'wd-num', key: 'num' }, String(position + 1)),
      e('span', { className: 'wd-meta', key: 'meta' }, [
        editing
          // The same list a ritual is added from, so a renamed one is still
          // something ops and the crew sheets call by that name.
          ? e('span', { className: 'wd-namepick', key: 'nm' },
              e(SearchSelect, {
                value: entry.name || '',
                placeholder: 'Type to search, for example chooda',
                options: ritualChoices || [],
                // A name off the list, an older free text rename or one the
                // catalogue has since dropped, still shows as the current one.
                fallbackText: entry.name || '',
                spares: spareRituals,
                spareNote: 'Did not find it? Pick the closest one.',
                onChange: (v) => {
                  if (v && v !== entry.name) onRename(entry.id, v);
                  onEdit(null);
                },
              }))
          : e('span', { className: 'wd-nm', key: 'nm' }, entry.name || 'New ritual'),
        e(
          'span',
          { className: `wd-tm${isTimingSet(entry) ? '' : ' is-unset'}`, key: 'tm' },
          timing
        ),
      ]),
      e('span', { className: 'wd-brk', key: 'brk' }),
      // A package event is named by the estimation, so only a ritual the
      // couple added can be renamed here.
      locked || pkg
        ? null
        : e(
            'button',
            {
              key: 'edit',
              type: 'button',
              className: `wd-editbtn${editing ? ' is-on' : ''}`,
              onClick: () => onEdit(editing ? null : entry.id),
            },
            editing ? 'Done' : 'Edit'
          ),
      e(
        'span',
        { className: `wd-badge ${pkg ? 'is-pkg' : 'is-ritual'}`, key: 'badge' },
        (isPhone ? KIND_CHIP_SHORT : KIND_CHIP)[pkg ? PACKAGE : RITUAL]
      ),
      // Whose event this is. Where the estimation left that open the couple
      // decides it here; where it did not, the badge only reports it.
      // Our own list rather than the browser's: a native select opens the
      // phone's system picker, which looks nothing like the form.
      entry.side_editable && !locked
        ? e('div', { className: `wd-sidepick is-${side}`, key: 'side' },
            e(PickSelect, {
              ariaLabel: `Whose event is ${entry.name || 'this ritual'}`,
              value: side,
              options: EVENT_SIDES,
              onChange: (value) => onSide(entry.id, value),
            }))
        : e(
            'span',
            { className: `wd-sidetag is-${side}`, key: 'side' },
            `${ORDER_SIDE_CHIP[side]}${isPhone ? '' : ' SIDE'}`
          ),
      // Only a ritual the couple added can go. A package event was sold on
      // the estimation, so taking it off the day is a coordinator's call.
      !locked && !pkg
        ? e(
            'button',
            {
              key: 'x',
              type: 'button',
              className: 'wd-xbtn',
              'aria-label': `Remove ${entry.name}`,
              onClick: () => onRemove(entry.id),
            },
            // The heavy multiplication X rather than the mathematical one,
            // which draws at about half its own size.
            '\u2715'
          )
        : null,
      // The server's own words, because only it knows what the estimation sold.
      error ? e('p', { className: 'wd-sideerror', key: 'err' }, error) : null,
      // The side is chosen here, so a side ops turned back is answered here
      // too. The control above already reads what the estimation sold,
      // because a refused ask is not the couple's answer any more.
      (entry.rejected || {}).side
        ? e('p', { className: 'wd-rejected-note', key: 'r-side' }, [
            e('strong', { key: 't' }, 'Rejected'),
            e('span', { key: 'w' },
              ` \u00b7 you asked for ${entry.rejected.side.value}`
              + (entry.rejected.side.reason
                ? ` \u00b7 ${entry.rejected.side.reason}` : '')),
          ])
        : null,
      // A ritual ops turned back stays here to be changed or removed.
      (entry.rejected || {}).added_ritual
        ? e('p', { className: 'wd-rejected-note', key: 'r-ritual' }, [
            e('strong', { key: 't' }, 'Rejected'),
            e('span', { key: 'w' },
              entry.rejected.added_ritual.reason
                ? ` \u00b7 ${entry.rejected.added_ritual.reason}`
                : ' \u00b7 ops has not said why yet'),
          ])
        : null,
      // A couple shot taken inside this event belongs on the card, not above
      // or below it.
      shot || null,
    ]
  );
};

export default OrderCard;
